import { useCallback, useMemo } from 'react'

import { runInAction, toJS } from 'mobx'

import { WorkflowsFilterType } from '~/client/graph'
import {
  useDeleteWorkflowCustomFilterMutation,
  useGetWorkflowCustomFiltersByProjectQuery,
  useSaveWorkflowCustomFilterMutation,
} from '~/client/graph/operations/generated/CustomWorkflowsFilter.generated'
import {
  BUILT_IN_FILTER_TYPES,
  DeleteFilterMutationFn,
  FilterTypeDescriptor,
  SaveFilterMutationFn,
} from '~/client/src/desktop/components/SavedViews'
import { workflowsFilterTypes } from '~/client/src/desktop/enums/WorkflowsFilterType'
import {
  formsGroupingOptionList,
  getLogisticsGroupingOptionTranslatorKey,
} from '~/client/src/shared/enums/LogisticsGroupingOption'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IFilters } from '~/client/src/shared/stores/InitialState'
import LogisticsFilterStore from '~/client/src/shared/stores/LogisticsFilter.store'
import {
  isLocationKeyedFilterType,
  matchLocationKeysToOptions,
} from '~/client/src/shared/utils/logisticsHelper'

const isFilterTypeRelevantForViewMode = (
  type: WorkflowsFilterType,
): boolean => {
  return workflowsFilterTypes.includes(type)
}

const WORKFLOW_FACETS: FilterTypeDescriptor[] = [
  {
    key: WorkflowsFilterType.Status,
    label: 'Status',
    type: BUILT_IN_FILTER_TYPES.WORKFLOW_STATUS,
  },
  {
    key: WorkflowsFilterType.Location,
    label: 'Location',
    type: BUILT_IN_FILTER_TYPES.LOCATION,
  },
  {
    key: WorkflowsFilterType.Equipment,
    label: 'Equipment',
    type: BUILT_IN_FILTER_TYPES.EQUIPMENT,
  },
  {
    key: WorkflowsFilterType.Company,
    label: 'Company',
    type: BUILT_IN_FILTER_TYPES.COMPANY,
  },
  {
    key: WorkflowsFilterType.ResponsibleContact,
    label: 'Responsible Contact',
    type: BUILT_IN_FILTER_TYPES.USER,
  },
  {
    key: WorkflowsFilterType.Category,
    label: 'Category',
    type: BUILT_IN_FILTER_TYPES.WORKFLOW_CATEGORY,
  },
  {
    key: WorkflowsFilterType.Type,
    label: 'Type',
    type: BUILT_IN_FILTER_TYPES.WORKFLOW_TYPE,
  },
  {
    key: WorkflowsFilterType.Project,
    label: 'Project',
    type: BUILT_IN_FILTER_TYPES.PROJECT,
  },
  {
    key: WorkflowsFilterType.Attachment,
    label: 'Attachment',
  },
]

interface UseWorkflowsSavedViewsParams {
  projectId: string
  logisticsFilterStore: LogisticsFilterStore
  filters: IFilters & any
}

export const useWorkflowsSavedViews = ({
  projectId,
  logisticsFilterStore,
  filters,
}: UseWorkflowsSavedViewsParams) => {
  const { data: savedViewsData } = useGetWorkflowCustomFiltersByProjectQuery({
    variables: { projectId },
    skip: !projectId,
  })

  const savedViews = useMemo(() => {
    return (
      savedViewsData?.workflowsCustomFilters?.data?.map(filter => ({
        id: filter.id,
        name: filter.name,
        isPublic: filter.isPublic,
        filtersByFilterType: filter.filtersByFilterType,
        groupBy: filter.groupBy,
        userId: filter.userId,
      })) || []
    )
  }, [savedViewsData])

  const appliedFilters = useMemo(() => {
    const result: { [facetKey: string]: Set<string> } = {}

    WORKFLOW_FACETS.forEach(facet => {
      const filterField = filters.fieldsMap[facet.key]
      if (filterField?.selectedFilterOptions) {
        const selectedOptions = toJS(filterField.selectedFilterOptions)
        if (selectedOptions instanceof Map) {
          result[facet.key] = new Set(Array.from(selectedOptions.keys()))
        } else {
          result[facet.key] = new Set()
        }
      } else {
        result[facet.key] = new Set()
      }
    })

    return result
  }, [toJS(filters.fieldsMap)])

  const handleApplyFilters = useCallback(
    (filtersToApply: { [key: string]: Set<string> }, groupBy: string) => {
      runInAction(() => {
        if (groupBy) {
          filters.groupingKey = groupBy
        }

        const stores = logisticsFilterStore.filterStoresByTypeMap

        Object.values(stores).forEach(filterStore => {
          filterStore.clickOnDeselectAll()
          filterStore.clickOnApply()
        })

        Object.entries(filtersToApply || {}).forEach(([type, valueSet]) => {
          if (!isFilterTypeRelevantForViewMode(type as WorkflowsFilterType)) {
            return
          }
          const filterStore = stores[type]
          if (!filterStore) {
            return
          }

          const savedKeys = Array.from(valueSet)
          // A saved `Kind|id` location key keeps the kind it was saved with: re-match
          // it to the live option by id, or a re-kind drops it from the view (STX1-597)
          filterStore.selectOptionsByIds(
            isLocationKeyedFilterType(type)
              ? matchLocationKeysToOptions(savedKeys, filterStore.allOptionIds)
              : savedKeys,
          )
          filterStore.clickOnApply()
        })

        logisticsFilterStore.syncFilters()
      })
    },
    [logisticsFilterStore, filters],
  )

  const currentGrouping = useMemo(() => {
    return filters.groupingKey || null
  }, [filters.groupingKey])

  const groupingOptions = useMemo(() => {
    return formsGroupingOptionList.map(option => ({
      key: option,
      label:
        Localization.translator[
          getLogisticsGroupingOptionTranslatorKey(option)
        ],
    }))
  }, [])

  const [saveFilterMutation] = useSaveWorkflowCustomFilterMutation()
  const [deleteFilterMutation] = useDeleteWorkflowCustomFilterMutation()

  return {
    savedViews,
    appliedFilters,
    handleApplyFilters,
    currentGrouping,
    facets: WORKFLOW_FACETS,
    groupingOptions,
    saveFilterMutation: saveFilterMutation as SaveFilterMutationFn,
    deleteFilterMutation: deleteFilterMutation as DeleteFilterMutationFn,
  }
}
